/**
 * Last line of defence before telemetry leaves the device. Call sites are
 * expected to log only IDs and classifications; these hooks make sure a missed
 * call site, an SDK-collected network error or a free-text error message still
 * cannot ship personal data, credentials or user content to Datadog.
 *
 * Application context (`ctx`) is allowlisted by field name: each approved field
 * has a validator that accepts only its expected format or controlled values.
 * Unknown fields are redacted, so a value is never kept merely because it looks
 * like an ID or a number. Log events are rebuilt from known SDK fields only.
 */

import { ReportReasonSchema, ReportTargetTypeSchema } from '@/api/report/types/report.interface';
import { FollowRequestStatus } from '@/api/user/types/FollowRequest';
import { FeedPostVisibilityValues } from '@/api/user/types/feed-post-visibility';
import { WORKOUT_EXIT_REASONS } from '@/api/workout/types/exit-reason';

export const REDACTED = '[redacted]';

type Validator = (value: unknown) => unknown;
type MutableEvent = Record<string, unknown>;

const MAX_DEPTH = 5;
const MAX_ARRAY_ITEMS = 50;

// ---------------------------------------------------------------------------
// Value validators
// ---------------------------------------------------------------------------

const oneOf =
  (...allowed: readonly unknown[]): Validator =>
  value =>
    allowed.includes(value) ? value : REDACTED;

const integerIn =
  (min: number, max: number): Validator =>
  value =>
    Number.isInteger(value) && (value as number) >= min && (value as number) <= max ? value : REDACTED;

const numberIn =
  (min: number, max: number): Validator =>
  value =>
    typeof value === 'number' && value >= min && value <= max ? value : REDACTED;

const boolean: Validator = value => (typeof value === 'boolean' ? value : REDACTED);

const matching =
  (pattern: RegExp): Validator =>
  value =>
    typeof value === 'string' && pattern.test(value) ? value : REDACTED;

const nullable =
  (validator: Validator): Validator =>
  value =>
    value === null || value === undefined ? value : validator(value);

/** MongoDB ObjectId, Firebase uid (28 chars, at least one letter) or UUID. */
const ENTITY_ID =
  /^(?:[a-f\d]{24}|(?=[A-Za-z\d]*[A-Za-z])[A-Za-z\d]{28}|[a-f\d]{8}-[a-f\d]{4}-[a-f\d]{4}-[a-f\d]{4}-[a-f\d]{12})$/i;
const entityId = matching(ENTITY_ID);

const SEMVER = /^\d{1,4}\.\d{1,4}\.\d{1,6}$/;
/** Constructor names such as `TypeError` or `ApiRequestError`, or a `typeof` result. */
const ERROR_NAME = /^(?:[A-Z][A-Za-z]{0,63}|string|number|object|undefined|boolean|symbol|bigint|function)$/;
const FIREBASE_ERROR_CODE = /^(?:auth|storage|messaging|firestore|functions|app|installations)\/[a-z\d-]{1,64}$/;
/** Firebase Web API keys are public (shipped in the bundle) and a monitor depends on them. */
const FIREBASE_WEB_API_KEY = /^AIza[\w-]{35}$/;
const PLAN_CATEGORY = /^[A-Za-z][\w-]{0,39}$/;
/** Static path words such as `user`, `fcm-token`, `seeAll`. */
const PATH_WORD = /^[a-z][A-Za-z-]{0,40}$/;
const HTTP_METHODS = ['GET', 'POST', 'PUT', 'PATCH', 'DELETE', 'HEAD', 'OPTIONS'];
const CAMERA_SOURCES = ['CAMERA', 'PHOTOS', 'PROMPT'];

/**
 * API endpoint as a route template: entity IDs become `:id`, any segment that
 * is neither a static word nor an ID becomes `:param`; query and fragment are
 * dropped. `user/66f.../follow?q=x` becomes `user/:id/follow`.
 */
export function sanitizeEndpoint(endpoint: string): string {
  const [path] = endpoint.split(/[?#]/);
  return path
    .split('/')
    .map(segment => {
      if (segment === '') return segment;
      if (ENTITY_ID.test(segment)) return ':id';
      return PATH_WORD.test(segment) ? segment : ':param';
    })
    .join('/');
}

const endpoint: Validator = value => (typeof value === 'string' ? sanitizeEndpoint(value) : REDACTED);

/** Bundle/script locations from window.onerror: scheme, host and a static path only. */
const scriptLocation: Validator = value => {
  if (typeof value !== 'string') return REDACTED;
  const location = sanitizeUrl(value);
  return /^(?:capacitor|ionic|https?):\/\/[\w.-]+(?::\d+)?\/[\w./-]*$/.test(location) ? location : REDACTED;
};

/** `source` is either a camera source (composer) or a script URL (window.onerror). */
const source: Validator = value => (CAMERA_SOURCES.includes(value as string) ? value : scriptLocation(value));

// ---------------------------------------------------------------------------
// Approved application context fields
// ---------------------------------------------------------------------------

/** Field containers whose children are checked against the same allowlist. */
const CONTAINER_FIELDS = new Set(['payload', 'params', 'appVersion']);

/**
 * Every field the app may log. Anything not listed here is redacted. IDs are
 * listed one by one (no `*Id` wildcard) and must look like real entity IDs.
 */
const APPROVED_FIELDS: Record<string, Validator> = {
  // HTTP diagnostics
  method: oneOf(...HTTP_METHODS),
  endpoint,
  statusCode: nullable(integerIn(100, 599)),
  errorName: matching(ERROR_NAME),
  code: matching(FIREBASE_ERROR_CODE),
  // Entity identifiers
  id: entityId,
  uid: entityId,
  userId: entityId,
  memberId: entityId,
  receiverId: entityId,
  targetId: entityId,
  targetUserId: entityId,
  postId: entityId,
  commentId: entityId,
  parentId: nullable(entityId),
  chatId: entityId,
  groupId: entityId,
  planId: entityId,
  exerciseId: entityId,
  workoutEventId: entityId,
  workoutId: entityId,
  planOrWorkoutEventId: entityId,
  alertId: entityId,
  alertIds: entityId,
  storyId: entityId,
  scopeId: entityId,
  // Controlled values
  kind: oneOf('gyming', 'fitfans'),
  status: oneOf(...Object.values(FollowRequestStatus)),
  reactionType: oneOf('like'),
  exitReasons: oneOf(...WORKOUT_EXIT_REASONS),
  reason: oneOf(...ReportReasonSchema.options),
  targetType: oneOf(...ReportTargetTypeSchema.options),
  feedPostVisibility: oneOf(...Object.values(FeedPostVisibilityValues)),
  scopeType: oneOf('global', 'group'),
  workoutFeedbackLevel: oneOf(1, 2, 3, 4),
  platform: oneOf('ios', 'android', 'web'),
  version: matching(SEMVER),
  source,
  // Flags and bounded numbers
  liked: boolean,
  hidden: boolean,
  enable: boolean,
  isActive: boolean,
  fromCheckpoint: boolean,
  forceUpdate: boolean,
  liveUpdate: boolean,
  limit: integerIn(1, 500),
  completePercentage: numberIn(0, 100),
  lineno: integerIn(0, 10_000_000),
  colno: integerIn(0, 10_000_000),
};

/**
 * Sensitive names are refused before the allowlist is consulted, so a future
 * allowlist entry can never re-enable them. Matching is substring-based and
 * case-insensitive (`newPassword`, `userEmail`, `phoneNumber`, ...).
 */
const SENSITIVE_FIELD =
  /pass(?:word|code)|token|secret|otp|e-?mail|phone|birth|\bdob\b|weight|height|injur|address|credential|cookie|authori[sz]ation|(?:full|first|last|display|user)_?name|caption|preview|message|content|body/i;

function sanitizeField(key: string, value: unknown, depth: number): unknown {
  if (SENSITIVE_FIELD.test(key)) return REDACTED;
  if (CONTAINER_FIELDS.has(key)) return sanitizeContainer(value, depth + 1);
  const validator = APPROVED_FIELDS[key];
  if (!validator) return REDACTED;
  if (Array.isArray(value)) return value.slice(0, MAX_ARRAY_ITEMS).map(item => validator(item));
  return validator(value);
}

function sanitizeContainer(value: unknown, depth: number): unknown {
  if (depth > MAX_DEPTH) return REDACTED;
  if (Array.isArray(value)) return value.slice(0, MAX_ARRAY_ITEMS).map(item => sanitizeContainer(item, depth + 1));
  if (!value || typeof value !== 'object') return REDACTED;
  return Object.fromEntries(Object.entries(value).map(([k, v]) => [k, sanitizeField(k, v, depth)]));
}

/** Sanitizes the application context passed to the logger (the `ctx` namespace). */
export function sanitizeContext(value: unknown): unknown {
  if (value === undefined) return undefined;
  return sanitizeContainer(value, 0);
}

// ---------------------------------------------------------------------------
// URLs and free text
// ---------------------------------------------------------------------------

const EMAIL = /[\w.%+-]+(?:@|%40)[a-z\d.-]+\.[a-z]{2,}/gi;
const BEARER = /\bBearer\s+[\w.~+/=-]+/gi;
const JWT = /\beyJ[\w-]+\.[\w-]+\.[\w-]*/g;
/** Firebase refresh tokens, FCM tokens, API keys and other opaque secrets. */
const OPAQUE_SECRET = /[\w-]{40,}/g;
const URL_IN_TEXT = /\b[a-z][a-z\d+.-]*:\/\/[^\s"'<>`()]+/gi;

const SAFE_QUERY_VALUES: Record<string, RegExp> = {
  category: PLAN_CATEGORY,
  scopeType: /^(?:global|group)$/,
  scopeId: ENTITY_ID,
  limit: /^\d{1,3}$/,
  key: FIREBASE_WEB_API_KEY,
};

/**
 * Keeps scheme, host and path; redacts every query value outside the
 * per-parameter allowlist and drops the fragment. Works on relative URLs.
 */
export function sanitizeUrl(url: string): string {
  const [withoutFragment] = url.split('#');
  const queryStart = withoutFragment.indexOf('?');
  const path = queryStart === -1 ? withoutFragment : withoutFragment.slice(0, queryStart);
  const query = queryStart === -1 ? '' : withoutFragment.slice(queryStart + 1);
  const safePath = path.replace(EMAIL, REDACTED).replace(OPAQUE_SECRET, REDACTED);

  const safeQuery = query
    .split('&')
    .filter(Boolean)
    .map(pair => {
      const separator = pair.indexOf('=');
      if (separator === -1) return pair.replace(OPAQUE_SECRET, REDACTED);
      const key = pair.slice(0, separator);
      const value = pair.slice(separator + 1);
      return SAFE_QUERY_VALUES[key]?.test(value) ? pair : `${key}=${REDACTED}`;
    })
    .join('&');
  return safeQuery ? `${safePath}?${safeQuery}` : safePath;
}

/**
 * Best-effort scrub for text that is already believed safe (engine messages,
 * stack frames, developer-written log messages). It is not a privacy boundary
 * on its own: untrusted free text is dropped, not scrubbed.
 */
export function scrubText(text: string): string {
  return text
    .replace(URL_IN_TEXT, url => sanitizeUrl(url))
    .replace(BEARER, `Bearer ${REDACTED}`)
    .replace(JWT, REDACTED)
    .replace(EMAIL, REDACTED)
    .replace(OPAQUE_SECRET, REDACTED);
}

// ---------------------------------------------------------------------------
// Errors
// ---------------------------------------------------------------------------

/** Messages written by the JS engine about code, not data. */
const ENGINE_ERROR_KINDS = new Set(['TypeError', 'ReferenceError', 'RangeError', 'EvalError', 'URIError']);

/** Fixed messages produced by browsers, the WebView or our own HTTP layer. */
const SAFE_ERROR_MESSAGES = [
  /^Failed to fetch$/,
  /^Load failed$/,
  /^NetworkError when attempting to fetch resource\.?$/,
  /^The (?:operation|request) (?:was|is) (?:aborted|cancelled|canceled)\.?$/i,
  /^The network connection was lost\.?$/,
  /^Script error\.?$/,
  /^ResizeObserver loop/,
  /^Failed to fetch dynamically imported module: /,
  /^Importing a module script failed\.?$/,
  /^Request failed$/,
  /^Unauthorized: session expired \((?:GET|POST|PUT|DELETE) [^)\s]+\)$/,
  /^(?:Empty response body|Non-JSON response) \(HTTP \d{3}\) from POST \S+$/,
  /^HTTP \d{3}\b[\w ]{0,40}$/,
];

/**
 * Structured classification instead of forwarding raw text: backend messages
 * (`ApiRequestError`), thrown strings, SyntaxErrors (which quote their input)
 * and anything unrecognised are redacted; the error kind is always kept.
 */
export function classifyErrorMessage(kind: unknown, message: unknown): string | undefined {
  if (typeof message !== 'string' || message === '') return undefined;
  if (kind === 'FirebaseError') {
    const code = /\(([a-z]+\/[a-z\d-]+)\)/.exec(message)?.[1];
    return code && FIREBASE_ERROR_CODE.test(code) ? `Firebase: (${code})` : REDACTED;
  }
  if (typeof kind === 'string' && ENGINE_ERROR_KINDS.has(kind)) return scrubText(message).slice(0, 300);
  if (SAFE_ERROR_MESSAGES.some(pattern => pattern.test(message))) return scrubText(message);
  return REDACTED;
}

const V8_FRAME = /^\s+at\s/;
const JSC_FRAME = /^[^\s@]*@\S+:\d+(?::\d+)?$|^[^\s@]+@\[native code\]$|^global code@/;

/**
 * Keeps stack frames (code locations) and rebuilds the header from the
 * classified message; any line that is not a frame (e.g. a message spanning
 * several lines) is dropped.
 */
export function sanitizeStack(stack: unknown, kind: unknown, classifiedMessage: string | undefined) {
  if (typeof stack !== 'string') return stack === undefined ? undefined : REDACTED;
  const frames = stack
    .split('\n')
    .filter(line => V8_FRAME.test(line) || JSC_FRAME.test(line.trim()))
    .map(line => scrubText(line));
  const header = typeof kind === 'string' && ERROR_NAME.test(kind) ? `${kind}: ${classifiedMessage ?? ''}` : null;
  return [header, ...frames].filter(line => line !== null).join('\n');
}

function sanitizeLogError(error: MutableEvent, isNetworkError: boolean): MutableEvent {
  const kind = matching(ERROR_NAME)(error.kind);
  const message = classifyErrorMessage(error.kind, error.message);
  const causes = Array.isArray(error.causes)
    ? error.causes.slice(0, 10).map(cause => {
        const c = (cause && typeof cause === 'object' ? cause : {}) as MutableEvent;
        const causeMessage = classifyErrorMessage(c.type, c.message);
        return {
          type: matching(ERROR_NAME)(c.type),
          source: c.source,
          message: causeMessage,
          stack: sanitizeStack(c.stack, c.type, causeMessage),
        };
      })
    : undefined;
  return {
    kind,
    message,
    // The Logs SDK puts the failed response body in the stack of network errors.
    stack: isNetworkError ? REDACTED : sanitizeStack(error.stack, error.kind, message),
    causes,
    handling: oneOf('handled', 'unhandled')(error.handling),
  };
}

// ---------------------------------------------------------------------------
// Datadog Logs
// ---------------------------------------------------------------------------

const LOG_STATUSES = ['debug', 'info', 'notice', 'warn', 'error', 'critical', 'alert', 'emerg', 'ok'];
const LOG_ORIGINS = ['logger', 'console', 'network', 'source', 'report', 'agent'];
const SDK_ID = /^[\w-]{1,64}$/;
const sdkId = matching(SDK_ID);
const APP_INFO_VALUE = /^[\w.+-]{0,64}$/;
const APP_INFO_FIELDS = [
  'platform',
  'native_version',
  'native_build',
  'bundle_kind',
  'bundle_id',
  'bundle_version',
  'environment',
];

const asRecord = (value: unknown): MutableEvent | undefined =>
  value && typeof value === 'object' && !Array.isArray(value) ? (value as MutableEvent) : undefined;

const idRecord = (value: unknown, fields: string[]) => {
  const record = asRecord(value);
  return record ? Object.fromEntries(fields.map(field => [field, sdkId(record[field])])) : undefined;
};

function sanitizeLogMessage(log: MutableEvent, http: MutableEvent | undefined, error: MutableEvent | undefined) {
  const message = typeof log.message === 'string' ? log.message : '';
  switch (log.origin) {
    case 'network':
      // SDK template "<Fetch|XHR> error <METHOD> <url>": rebuilt from structured fields.
      return `Network error ${oneOf(...HTTP_METHODS)(http?.method)} ${typeof http?.url === 'string' ? sanitizeUrl(http.url) : ''}`.trim();
    case 'console':
      // console.error arguments are arbitrary text and objects.
      return 'console.error (message redacted)';
    case 'source':
      return `Uncaught ${classifyErrorMessage(error?.kind, error?.message) ?? REDACTED}`;
    default:
      // Developer-written log messages: static strings with IDs at most.
      return scrubText(message).slice(0, 500);
  }
}

/**
 * Datadog Logs `beforeSend`. Rebuilds the event from known SDK fields plus the
 * sanitized `ctx` namespace; every other field is dropped. Returns false (drop
 * the log) if anything unexpected happens rather than sending it raw.
 */
export function sanitizeLogEvent(event: object): boolean {
  if (Array.isArray(event)) return false;
  const log = event as MutableEvent;
  try {
    const http = asRecord(log.http);
    const error = asRecord(log.error);
    const view = asRecord(log.view);
    const appInfo = asRecord(log.app_info);

    const safe: MutableEvent = {
      service: oneOf('gyming-app')(log.service),
      status: oneOf(...LOG_STATUSES)(log.status),
      date: typeof log.date === 'number' ? log.date : undefined,
      origin: oneOf(...LOG_ORIGINS)(log.origin),
      session_id: log.session_id === undefined ? undefined : sdkId(log.session_id),
      session: idRecord(log.session, ['id']),
      application_id: log.application_id === undefined ? undefined : sdkId(log.application_id),
      user_action: idRecord(log.user_action, ['id']),
      usr: idRecord(log.usr, ['id', 'anonymous_id']),
      account: idRecord(log.account, ['id']),
      view: view && {
        id: sdkId(view.id),
        url: typeof view.url === 'string' ? sanitizeUrl(view.url) : undefined,
        referrer: typeof view.referrer === 'string' ? sanitizeUrl(view.referrer) : undefined,
      },
      logger: idRecord(log.logger, ['name']),
      message: sanitizeLogMessage(log, http, error),
      error: error && sanitizeLogError(error, log.origin === 'network'),
      http: http && {
        method: oneOf(...HTTP_METHODS)(http.method),
        status_code: integerIn(0, 599)(http.status_code),
        url: typeof http.url === 'string' ? sanitizeUrl(http.url) : undefined,
      },
      app_info:
        appInfo &&
        Object.fromEntries(APP_INFO_FIELDS.map(field => [field, nullable(matching(APP_INFO_VALUE))(appInfo[field])])),
      ctx: sanitizeContext(log.ctx),
    };

    for (const key of Object.keys(log)) delete log[key];
    for (const [key, value] of Object.entries(safe)) {
      if (value !== undefined) log[key] = value;
    }
    return true;
  } catch {
    return false;
  }
}

// ---------------------------------------------------------------------------
// Datadog RUM
// ---------------------------------------------------------------------------

/**
 * Datadog RUM `beforeSend`. Only touches fields the SDK lets us modify
 * (resource/error URLs, error message and stack); views, actions, sessions and
 * long tasks pass untouched. `error.causes` is not modifiable in RUM.
 */
export function sanitizeRumEvent(rumEvent: object): boolean {
  const event = rumEvent as MutableEvent;
  try {
    const resource = asRecord(event.resource);
    if (event.type === 'resource' && resource && typeof resource.url === 'string') {
      resource.url = sanitizeUrl(resource.url);
    }

    const error = asRecord(event.error);
    if (event.type === 'error' && error) {
      const message = classifyErrorMessage(error.type, error.message);
      error.message = message ?? REDACTED;
      if (typeof error.stack === 'string') error.stack = sanitizeStack(error.stack, error.type, message);
      const errorResource = asRecord(error.resource);
      if (errorResource && typeof errorResource.url === 'string') {
        errorResource.url = sanitizeUrl(errorResource.url);
      }
    }
    return true;
  } catch {
    return false;
  }
}
